import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

// Exact layout matching the screenshot UI
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: "Helvetica",
    color: "#000000",
    backgroundColor: "#ffffff",
  },
  // Company Header
  headerContainer: {
    alignItems: "center",
    marginBottom: 15,
    lineHeight: 1.3,
  },
  topTitle: {
    fontSize: 11,
    fontWeight: "bold",
    textDecoration: "underline",
    marginBottom: 2,
  },
  companyName: {
    fontSize: 16,
    fontWeight: "bold",
  },
  companyAddress: {
    fontSize: 9,
    textAlign: "center",
    maxWidth: 500,
  },
  boldText: {
    fontWeight: "bold",
  },
  
  // Invoice No & Date Row
  metaRowContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 10,
  },
  metaHalfCell: {
    width: "50%",
    padding: 5,
    flexDirection: "row",
  },
  metaHalfCellRight: {
    width: "50%",
    padding: 5,
    flexDirection: "row",
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
  },

  // Billed To / Delivered To Block
  addressBlockContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 10,
  },
  addressCol: {
    width: "50%",
    padding: 5,
    minHeight: 65,
  },
  addressColRight: {
    width: "50%",
    padding: 5,
    minHeight: 65,
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
  },
  addressLine: {
    marginBottom: 3,
  },

  // Table Structure
  tableContainer: {
    borderWidth: 1,
    borderColor: "#000000",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    fontWeight: "bold",
    backgroundColor: "#ffffff",
    alignItems: "center",
    textAlign: "center",
    height: 18,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 18,
  },

  // Table Column Specific Widths & Borders
  cellSno: { width: "6%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "center", padding: 2 },
  cellHsn: { width: "12%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "center", padding: 2 },
  cellProduct: { width: "37%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "left", paddingLeft: 5, paddingVertical: 2 },
  cellBox: { width: "11%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "right", paddingRight: 5, paddingVertical: 2 },
  cellPrice: { width: "11%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "right", paddingRight: 5, paddingVertical: 2 },
  cellDiscount: { width: "10%", borderRightWidth: 1, borderRightColor: "#000000", textAlign: "right", paddingRight: 5, paddingVertical: 2 },
  cellAmount: { width: "13%", textAlign: "right", paddingRight: 5, paddingVertical: 2 },

  // Empty Spacer Row to push total section downward like paper templates
  blankSpaceRow: {
    height: 120, 
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    flexDirection: "row",
  },
  blankSpaceCell: {
    borderRightWidth: 1,
    borderRightColor: "#000000",
  },

  // Totals Area
  totalsSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalsLabelBox: {
    width: "25%",
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    padding: 3,
    textAlign: "right",
    paddingRight: 10,
  },
  totalsValueBox: {
    width: "13%",
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    padding: 3,
    textAlign: "right",
    paddingRight: 5,
  },

  // Bottom Footer Blocks
  bottomContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000000",
    borderTopWidth: 0,
    minHeight: 70,
  },
  wordsBlock: {
    width: "48%",
    padding: 5,
  },
  vehicleBlock: {
    width: "22%",
    padding: 5,
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
  },
  signBlock: {
    width: "30%",
    padding: 5,
    borderLeftWidth: 1,
    borderLeftColor: "#000000",
    justifyContent: "space-between",
    alignItems: "center",
  },
  disclaimer: {
    fontSize: 7,
    color: "#555555",
    marginTop: 15,
    textAlign: "center",
  }
});

// Helper function to calculate total amount in words (Simple Fallback)
const amountToWords = (amount) => {
  const num = Number(amount);

  if (!Number.isFinite(num) || num <= 0) {
    return "ZERO RUPEES ONLY";
  }

  const ones = [
    "",
    "ONE",
    "TWO",
    "THREE",
    "FOUR",
    "FIVE",
    "SIX",
    "SEVEN",
    "EIGHT",
    "NINE",
    "TEN",
    "ELEVEN",
    "TWELVE",
    "THIRTEEN",
    "FOURTEEN",
    "FIFTEEN",
    "SIXTEEN",
    "SEVENTEEN",
    "EIGHTEEN",
    "NINETEEN",
  ];

  const tens = [
    "",
    "",
    "TWENTY",
    "THIRTY",
    "FORTY",
    "FIFTY",
    "SIXTY",
    "SEVENTY",
    "EIGHTY",
    "NINETY",
  ];

  const twoDigits = (n) => {
    n = Number(n);

    if (n < 20) {
      return ones[n];
    }

    const ten = Math.floor(n / 10);
    const unit = n % 10;

    return (
      tens[ten] +
      (unit > 0 ? " " + ones[unit] : "")
    );
  };

  const threeDigits = (n) => {
    n = Number(n);

    if (n < 100) {
      return twoDigits(n);
    }

    const hundred = Math.floor(n / 100);
    const remainder = n % 100;

    return (
      ones[hundred] +
      " HUNDRED" +
      (remainder > 0
        ? " AND " + twoDigits(remainder)
        : "")
    );
  };

  const integerPart = Math.floor(num);

  const paise = Math.round(
    (num - integerPart) * 100
  );

  let result = "";

  // CRORE
  const crore = Math.floor(
    integerPart / 10000000
  );

  let remainder = integerPart % 10000000;

  if (crore > 0) {
    result +=
      threeDigits(crore) +
      " CRORE";
  }

  // LAKH
  const lakh = Math.floor(
    remainder / 100000
  );

  remainder = remainder % 100000;

  if (lakh > 0) {
    result +=
      (result ? " " : "") +
      twoDigits(lakh) +
      " LAKH";
  }

  // THOUSAND
  const thousand = Math.floor(
    remainder / 1000
  );

  remainder = remainder % 1000;

  if (thousand > 0) {
    result +=
      (result ? " " : "") +
      twoDigits(thousand) +
      " THOUSAND";
  }

  // HUNDRED
  if (remainder > 0) {
    result +=
      (result ? " " : "") +
      threeDigits(remainder);
  }

  if (!result) {
    result = "ZERO";
  }

  // PAISE
  if (paise > 0) {
    result +=
      " AND " +
      twoDigits(paise) +
      " PAISE";
  }

  return result + " RUPEES ONLY";
};

export default function InvoicePDF({ invoice = {}, items = [] }) {
  // Safety Mappings matching your API data layout
  const invoiceNo = invoice?.invoice_no || "INV - 0";
  const dateStr = invoice?.created_at ? invoice.created_at.slice(0, 10) : "";
  
  const customerName = invoice?.customer_name || "";
  const address = invoice?.address || "";
  const mobileNo = invoice?.phone || "";
  const gstin = invoice?.gst_no || "";

  const companyName =
  invoice?.branch_company_name ||
  "";

const companyAddress =
  invoice?.branch_address || "";

const companyPhone =
  invoice?.branch_phone || "";

const companyGst =
  invoice?.branch_gst_no || "";


  // Financial Summary Computations
  // const subTotal = Number(invoice?.sub_total || items.reduce((acc, item) => acc + (item.price * (item.box || item.qty || 1)), 0)).toFixed(2);
  // const cgst = Number(invoice?.cgst || (subTotal * 0.09)).toFixed(2);
  // const sgst = Number(invoice?.sgst || (subTotal * 0.09)).toFixed(2);
  const roundOff = Number(invoice?.round_off || 0).toFixed(2);
  // const grandTotal = Number(invoice?.net_total || (parseFloat(subTotal) + parseFloat(cgst) + parseFloat(sgst))).toFixed(2);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        
        {/* 1. COMPANY HEADER */}
        <View style={styles.headerContainer}>
          <Text style={styles.topTitle}>INVOICE</Text>
          <Text style={styles.companyName}>{companyName}</Text>
          <Text style={styles.companyAddress}>
            {companyAddress}
          </Text>
          <Text style={styles.boldText}>GSTIN : {companyGst}</Text>
          <Text>Mobile : {companyPhone}</Text>
        </View>

        {/* 2. INVOICE NO & DATE ROW */}
        <View style={styles.metaRowContainer}>
          <View style={styles.metaHalfCell}>
            <Text style={styles.boldText}>Invoice No   : </Text>
            <Text>{invoiceNo}</Text>
          </View>
          <View style={styles.metaHalfCellRight}>
            <Text style={styles.boldText}>Date : </Text>
            <Text>{dateStr}</Text>
          </View>
        </View>

        {/* 3. CLIENT ADDRESS BLOCKS */}
        <View style={styles.addressBlockContainer}>
          <View style={styles.addressCol}>
            <Text style={[styles.boldText, styles.addressLine]}>Billed to : {customerName}</Text>
            <Text style={styles.addressLine}>{address}</Text>
            <Text style={styles.addressLine}>Party Mobile No : {mobileNo}</Text>
            <Text style={styles.addressLine}>GST IN / UIN : {invoice?.gst_no || ""}</Text>
          </View>
          <View style={styles.addressColRight}>
            <Text style={[styles.boldText, styles.addressLine]}>Delivered to : {customerName}</Text>
            <Text style={styles.addressLine}>{address}</Text>
            <Text style={styles.addressLine}>Party Mobile No : {mobileNo}</Text>
            <Text style={styles.addressLine}>GST IN / UIN : {gstin}</Text>
          </View>
        </View>

        {/* 4. PRODUCTS DATA GRID TABLE */}
        <View style={styles.tableContainer}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.cellSno}>S.NO</Text>
            <Text style={styles.cellHsn}>HSN CODE</Text>
            <Text style={styles.cellProduct}>PRODUCTS</Text>
            <Text style={styles.cellBox}>BOX</Text>
            <Text style={styles.cellPrice}>PRICE</Text>
            <Text style={styles.cellDiscount}>DISCOUNT</Text>
            <Text style={styles.cellAmount}>AMOUNT</Text>
          </View>

          {/* Dynamic Map Body Rows */}
          {items.map((item, index) => {
            const currentBox = Number(item.box || item.qty || 0).toFixed(2);
            const currentPrice = Number(item.price || 0).toFixed(2);
            const currentDiscount = Number(item.discount || 0).toFixed(2);
            

            return (
              <View style={styles.tableRow} key={index}>
                <Text style={styles.cellSno}>{index + 1}</Text>
                <Text style={styles.cellHsn}>{item.hsn_code || ""}</Text>
                <Text style={styles.cellProduct}>{item.product_name || "PRODUCT NAME"}</Text>
                <Text style={styles.cellBox}>{currentBox}</Text>
                <Text style={styles.cellPrice}>{currentPrice}</Text>
                <Text style={styles.cellDiscount}>{currentDiscount}</Text>
                <Text style={styles.cellAmount}>{(Number(item?.item_amount) || 0).toFixed(2)}</Text>
              </View>
            );
          })}

          {/* Spacer Row to precisely match look of traditional bills */}
          <View style={styles.blankSpaceRow}>
            <View style={styles.cellSno}></View>
            <View style={styles.cellHsn}></View>
            <View style={styles.cellProduct}></View>
            <View style={styles.cellBox}></View>
            <View style={styles.cellPrice}></View>
            <View style={styles.cellDiscount}></View>
            <View style={styles.cellAmount}></View>
          </View>

          {/* 5. CALCULATIONS SUMMARY BLOCK */}
          <View style={styles.totalsSection}>
            <Text style={[styles.totalsLabelBox, styles.boldText]}>SUB TOTAL</Text>
            <Text style={styles.totalsValueBox}>{(Number(invoice?.total_amount) || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.totalsSection}>
            <Text style={[styles.totalsLabelBox, styles.boldText]}>CGST 9%</Text>
           <Text style={styles.totalsValueBox}>
  {(Number(invoice?.cgst) || 0).toFixed(2)}
</Text>
          </View>
          <View style={styles.totalsSection}>
            <Text style={[styles.totalsLabelBox, styles.boldText]}>SGST 9%</Text>
            <Text style={styles.totalsValueBox}>
  {(Number(invoice?.sgst) || 0).toFixed(2)}
</Text>
          </View>
          <View style={styles.totalsSection}>
            <Text style={[styles.totalsLabelBox, styles.boldText]}>ROUND OFF</Text>
            <Text style={styles.totalsValueBox}>{roundOff}</Text>
          </View>
          <View style={styles.totalsSection}>
            <Text style={[styles.totalsLabelBox, styles.boldText, { fontSize: 10 }]}>TOTAL</Text>
            <Text style={[styles.totalsValueBox, styles.boldText, { fontSize: 10 }]}> {(Number(invoice?.net_total) || 0).toFixed(2)}</Text>
          </View>
        </View>

        {/* 6. BOTTOM FOOTER SECTION (Amount in words, Vehicle No, Signatory) */}
        <View style={styles.bottomContainer}>
          <View style={styles.wordsBlock}>
            <Text style={styles.boldText}>{amountToWords(Number(invoice.net_total))}</Text>
          </View>
          <View style={styles.vehicleBlock}>
            <Text style={styles.boldText}>Vehicle No: {invoice?.vehicle_no || ""}</Text>
          </View>
          <View style={styles.signBlock}>
            <Text style={styles.boldText}>For {companyName}</Text>
            <Text style={styles.boldText}>Authorised signatory</Text>
          </View>
        </View>

        <Text style={styles.disclaimer}>This is computer generated document, and does not require a signature</Text>

      </Page>
    </Document>
  );
}